import React, { memo, useContext, useMemo, useState, useEffect } from 'react';
import { Box, Flex, Heading } from '@chakra-ui/layout';
import { Grid, GridItem } from '@chakra-ui/react';
import { BodyProps } from 'constants/MultipleAcorddions';
import styled, { css } from 'styled-components';
import { Label, UnfilledError } from 'components';
import { ThirdPartyAssessmentCtx } from 'pages';
import { colors } from 'theme/colors';
import { WarningMessage } from 'components/WarningMessage';
import { useBrokenText } from 'hooks/useBrokenText';
import { useBoolean } from 'hooks/useBoolean';
import { Sbutton } from './styles';
import { useParams } from 'react-router-dom';
import { getTPAFinishPosition } from 'services/TPAConclusion';

interface IParams {
  id: string;
}

const velImg = require('assets/images/rectangle_bar.svg').default;
const pontImg = require('assets/images/ponteiro.svg').default;

const getScenery = (e1: string, e2: string) => {
  if (e1.match(/controlador/gi) && e2.match(/operador/gi)) {
    return [
      'Como controladora, a {{client}} atrai o nível máximo de responsabilidade pelo tratamento de dados. Além de responsabilizar-se pelas condutas próprias, tem o dever de supervisionar o operador.',
      'Em síntese: entendemos que a {{client}} deve impor-se de maneira mais dura na negociação contratual, exigindo a assunção de responsabilidades mínimas por parte do operador, sem tanta margem negociação.',
    ];
  } else if (e1.match(/operador/gi) && e2.match(/controlador/gi)) {
    return [
      'Pelo trabalho de mapeamento realizado até o momento (setembro/2020) na {{client}}, não idenficaram-se situações em que esse cenário se aplicaria. Contudo, na eventualidade de surgirem, seguem nossas percepções abaixo:',
      'Como operador, a {{client}} goza de maior conforto em relação ao grau de responsabilidade assumido.',
      'Nesse contexto, entendemos que a {{client}} deve negociar contratos a fim de repelir o aumento de responsabilidade natural do operador (atenção para questões que podem ser atribuídas à {{client}}, como: coleta de consentimento, atendimento aos direitos dos titulares, realização de relatório de impacto e comunicação em caso de incidentes).',
    ];
  } else if (e1.match(/controlador/gi) && e2.match(/controlador/gi)) {
    return [
      'Em uma relação controladora/controladora, há maior margem para negociação e distribuição de responsabilidades, as quais recaem primordialmente sobre ambas as partes.',
      'Em síntese: entendemos que a {{client}} deve preparar-se para discussões mais intensas e caso a caso em torno do estabelecimento e divisão de obrigações próprias de controlador entre as partes.',
    ];
  } else return [];
};

const getMaturityLevel = (
  value: React.ReactText,
): {
  text: 'Incipiente' | 'Moderado' | 'Robusto';
  color: 2700 | 2800 | 2900;
} => {
  const level = parseFloat(value + '');
  if (level <= 59.99) {
    return {
      text: 'Incipiente',
      color: 2900,
    };
  } else if (level <= 75.99) {
    return {
      text: 'Moderado',
      color: 2800,
    };
  } else {
    return {
      text: 'Robusto',
      color: 2700,
    };
  }
};

const getRiskLevel = (
  value: React.ReactText,
  client: string,
): {
  text: 'Risco Alto' | 'Risco Significativo' | 'Risco Moderado' | 'Risco Reduzido';
  color: 2700 | 2200 | 'tangerine' | 2900;
  infos: Array<string | React.ReactNode>;
} => {
  const level = parseFloat(value + '');
  if (level <= 9.99) {
    return {
      text: 'Risco Reduzido',
      color: 2700,
      infos: [
        <>
          Observação: somente enviar a notificação elaborada, de caráter genérico, para que a outra
          parte observe a LGPD.
          <strong>
            Versão simplificada do DPA, se o caso: caso entenda-se pela aplicação de cláusulas,
            recomenda-se a utilização de versão simplificada, visando reduzir o ônus operacional de
            se negociar cláusula mais robusta.
          </strong>
          Alta margem de negociação: no caso de negociação de cláusulas de proteção de dados, há
          alta margem para flexibilização da cláusula proposta ou para aceitação de cláusulas
          propostas pela outra parte.
        </>,
      ],
    };
  } else if (level <= 39.99) {
    return {
      text: 'Risco Moderado',
      color: 'tangerine',
      infos: [
        <>
          <strong>Aplicar versão simplificada da cláusula</strong>: recomenda-se a utilização de
          versão simplificada da cláusula, visando reduzir o ônus operacional de se negociar
          cláusula mais robusta.
        </>,
        <>
          <strong>Razoável margem de negociação</strong>: há razoável margem para flexibilização da
          cláusula proposta ou para aceitação de cláusulas propostas pela outra parte.
        </>,
      ],
    };
  } else if (level <= 49.99) {
    return {
      text: 'Risco Significativo',
      color: 2200,
      infos: [
        <>
          <strong>Aplicar versão intermediária da cláusula</strong>: aplicar versão intermediária da
          cláusula; preferencialmente, não aceitar a proposição de cláusulas pela outra parte.
        </>,
        <>
          <strong>Reduzida margem de negociação</strong>: diante do risco apontado, reduzir a
          possibilidade de flexibilização da cláusula. Caso a minuta seja da parte contrária, ser
          mais criterioso quanto à análise e propor maiores mudanças.
        </>,
        <>
          <strong>Avaliação de viabilidade jurídica</strong>: verificar a necessidade de submeter a
          operação amparada pelo contrato à avaliação de viabilidade jurídica (seja no contexto ou
          não de um Relatório de Impacto).
        </>,
        <>Recomenda-se o apoio do DPO/Equipe de Proteção de Dados da {client} ou externo.</>,
      ],
    };
  } else {
    return {
      text: 'Risco Alto',
      color: 2900,
      infos: [
        <>
          <strong>Aplicar versão robusta da cláusula:</strong>
          aplicar versão robusta da cláusula. Aceitar a proposição de cláusulas pela outra parte
          somente em casos excepcionais.
        </>,
        <>
          <strong>Mínima margem de negociação</strong>: diante do risco, flexibilizar apenas
          aspectos pontuais do DPA, como indenização por danos indiretos. Se excepcionalmente a
          minuta vier da outra parte, realizar rigoroso comparativo com a cláusula robusta, para
          sugerir toda e qualquer alteração para reduzir a distância entre os documentos.
        </>,
        <>
          <strong>Avaliação de viabilidade jurídica</strong>: diante do risco apontado, submeter a
          operação amparada pelo contrato à avaliação de viabilidade jurídica (seja no contexto ou
          não de um Relatório de Impacto).
        </>,
        <>Recomenda-se o apoio do DPO/Equipe de Proteção de Dados da {client} ou externo.</>,
      ],
    };
  }
};

const pointerStartRight = css<{ pos: string | number }>`
  right: ${({ pos }) => (+pos - 2).toString() + '%'};
`;
const pointerStartLeft = css<{ pos: string | number }>`
  left: ${({ pos }) => (+pos - 2).toString() + '%'};
`;
const resultStartRight = css<{ pos: string | number }>`
  right: ${({ pos }) =>
    (+pos <= 94 ? (+pos <= 1.5 ? +pos - 5 : +pos - 7) : 94 - 7).toString() + '%'};
`;
const resultStartLeft = css<{ pos: string | number }>`
  left: ${({ pos }) =>
    (+pos <= 94 ? (+pos <= 1.5 ? +pos - 5 : +pos - 7) : 94 - 7).toString() + '%'};
`;

// const testePos = 36;
const PontGraph = styled.div<{ pos: string | number; start: 'left' | 'right' }>`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: fit-content;
  height: fit-content;
  padding-top: 50px;
  img.pointer {
    position: absolute;
    top: 75%;
    ${({ start }) => (start === 'right' ? pointerStartRight : pointerStartLeft)}
  }
  .result {
    position: absolute;
    bottom: 50%;
    ${({ start }) => (start === 'right' ? resultStartRight : resultStartLeft)}
    width: fit-content;
  }
`;

interface IProps extends BodyProps {
  isDone?: boolean;
  unfilledSteps: string[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const Item: React.FC<IProps> = ({ isDone, unfilledSteps }) => {
  const params = useParams<IParams>();

  const ctx = useContext(ThirdPartyAssessmentCtx);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isEffect, setIsEffect] = useState<number>(Math.random());

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { load, setIsLoad } = useContext(ThirdPartyAssessmentCtx);
  const [unfilledStepRef, setUnfilledStepRef] = useState<string[]>([]);
  const unfilled: number[] = [2, 3, 4];

  const unfilledStep: string[] = [
    '2 - Avaliação de Maturidade',
    '3 - Teste Controlador-Operador',
    '4 - Teste de Risco de Contrato',
  ];

  // eslint-disable-next-line prefer-const
  let unfilledStepsRef: string[] = [];

  const getFormConclusions = async (finishForm: number) => {
    const id = Number(params.id);
    const response = await getTPAFinishPosition(finishForm, id);
    if (!response.data) {
      if (unfilledStep[finishForm - 2].slice(0, 1) === String(finishForm)) {
        unfilledStepsRef.push(unfilledStep[finishForm - 2]);
        setIsEffect(Math.random());
      }
    } else {
      const index = unfilledStepsRef.indexOf(unfilledStep[finishForm - 2]);
      if (index > -1) unfilledStepsRef.splice(index, 1);
    }
    setUnfilledStepRef([...unfilledStepsRef]);
  };

  const dpo = ctx.data.dpo || '';
  const [pieceText, fullText] = useBrokenText(dpo, 195);
  const [hide, toggleHide] = useBoolean(true);
  const maturity = useMemo(
    () => getMaturityLevel(ctx.results?.maturityScore.result || 0),
    [ctx.results?.maturityScore],
  );
  const scenery = useMemo(
    () =>
      getScenery(
        ctx.results?.companyPositionResult || '',
        ctx.results?.thirdPartyPositionResult || '',
      ),
    [ctx.results],
  );
  const risk = useMemo(
    () => getRiskLevel(ctx.results?.contractRiskResult.result || '', ctx.client?.name || ''),
    [ctx],
  );

  useEffect(() => {
    // (() => {
    // eslint-disable-next-line no-extra-boolean-cast
    if (!!params.id && !load) {
      if (
        (ctx.data.status === 'DONE' &&
          (unfilledSteps.length !== 0 || unfilledStepsRef.length !== 0)) ||
        ctx.data.status === 'DRAFT'
      ) {
        unfilled.forEach(item => {
          getFormConclusions(item);
        });
      }
    }
    // })();
  }, [load]);

  // eslint-disable-next-line no-extra-boolean-cast
  if (!!params.id) {
    if (unfilledStepRef.length > 0) {
      return <UnfilledError unfilledSteps={unfilledStepRef.sort()} />;
    }
  }

  if (unfilledSteps.length > 0) {
    return <UnfilledError unfilledSteps={unfilledSteps} />;
  }

  if (unfilledStepRef.length === 0) {
    if (params.id) {
      // console.log('2');
      // console.log(params);

      return (
        <Flex alignItems="flex-start" marginTop="1rem" width="100%" direction="column">
          <Heading color="brand.tertiary" mb="24px" size="md" as="h3">
            Demais Recomendações
          </Heading>
          <Grid templateColumns="repeat(4, 1fr)">
            <GridItem>
              <Label>Objeto do Contrato:</Label>
              <p>{ctx.data.areaResponsable}</p>
              <Box mt="1rem" />
              <Label>Data de Assinatura:</Label>
              <p>{ctx.data.receiveDate || 'Contrato não assinado'}</p>
            </GridItem>
            <GridItem>
              <Label>Contratante:</Label>
              <p>{ctx.data.companyName}</p>
              <Box mt="1rem" />
              <Label>Data de Expiração:</Label>
              <p>{ctx.data.reviewDate || 'Prazo Indeterminado'}</p>
            </GridItem>
            <GridItem colSpan={2}>
              <Label>Contratado:</Label>
              <p>{ctx.data.contact}</p>
              <Box mt="1rem" />
              <Label>Lista de Dados Pessoais Envolvidos na Contratação:</Label>
              <p>{hide ? pieceText + (fullText.length > 195 ? '...' : '') : fullText}</p>
              {fullText.length > 195 && (
                <Sbutton onClick={() => toggleHide()}>{hide ? 'Ver Todos' : 'Esconder'}</Sbutton>
              )}
            </GridItem>
          </Grid>
          <Box my="24px" width="100%" height="1px" background={colors.brand[600]} />
          <Heading color="brand.tertiary" mb="24px" size="md" as="h3">
            Maturidade do terceiro
          </Heading>
          <Flex direction="row">
            <PontGraph pos={ctx.results?.maturityScore.result || 0} start="right">
              <img alt="Velocimetro" src={velImg} />
              <img className="pointer" alt="Ponteiro" src={pontImg} />
              <span className="result">
                <Box>
                  <Heading as="h4" size="sm">
                    {maturity.text}
                  </Heading>
                  <Heading as="h4" size="lg" color={colors.brand[maturity.color]}>
                    {parseFloat(ctx.results?.maturityScore.result || '0').toFixed(2)}%
                  </Heading>
                </Box>
              </span>
            </PontGraph>
          </Flex>
          <Heading color="brand.tertiary" mt="64px" as="h4" fontSize="18px" mb="16px">
            Controlador/Operador:
          </Heading>
          <Flex gridGap="60px" mb="16px" alignItems="flex-start" direction="row">
            <Box>
              <Label>Posição Exercida pela {ctx.client?.name}:</Label>
              <p>{ctx.results?.companyPositionResult}</p>
            </Box>
            <Box>
              <Label>Posição Exercida pela outra parte:</Label>
              <p>{ctx.results?.thirdPartyPositionResult}</p>
            </Box>
          </Flex>
          <Box
            mb="32px"
            width="100%"
            p="16px"
            border="1px solid"
            borderColor="brand.gray600"
            borderRadius="4px"
          >
            {scenery.map((i, k) => (
              <Box as="p" mb="8px" key={k}>
                {i.replaceAll('{{client}}', ctx.client?.name || '')}
              </Box>
            ))}

            <Box as="p">
              Pelo trabalho de mapeamento realizado até o momento (mês/ano) na {ctx.client?.name},
              não identificaram-se situações em que esse cenário se aplicaria. Contudo, na
              eventualidade de surgirem, seguem nossas percepções abaixo:Como operador, a{' '}
              {ctx.client?.name} goza de maior conforto em relação ao grau de responsabilidade
              assumido.Nesse contexto, entendemos que a {ctx.client?.name} deve negociar contratos a
              fim de repelir o aumento de responsabilidade natural do operador (atenção para
              questões que podem ser atribuídas à {ctx.client?.name}, como: coleta de consentimento,
              atendimento aos direitos dos titulares, realização de relatório de impacto e
              comunicação em caso de incidentes).{' '}
              <b>
                Seja qual for o resultado, é recomendável: Avaliar a criticidade do contrato (Teste
                de Risco do Contrato)Validar este documento e suas conclusões com o Encarregado pelo
                Tratamento de Dados Pessoais (DPO) da {ctx.client?.name}
              </b>
            </Box>
          </Box>
          <Heading color="brand.tertiary" as="h4" fontSize="18px" mb="24px">
            Risco do Contrato:
          </Heading>
          <Flex direction="row">
            <PontGraph pos={ctx.results?.contractRiskResult.result || 0} start="left">
              <img alt="Velocimetro" src={velImg} />
              <img className="pointer" alt="Ponteiro" src={pontImg} />
              <span className="result">
                <Box>
                  <Heading as="h4" size="sm">
                    {risk.text}
                  </Heading>
                  <Heading as="h4" size="lg" color={colors.brand[risk.color]}>
                    {parseFloat(ctx.results?.contractRiskResult.result || '0').toFixed(2)}%
                  </Heading>
                </Box>
              </span>
            </PontGraph>
          </Flex>
          {+parseFloat(ctx.results?.contractRiskResult.result || '0').toFixed(2) >= 50 && (
            <WarningMessage
              mt="31"
              text={'Considerar realização de Relatório de Impacto de Proteção de Dados Pessoais'}
            />
          )}
          <Box
            mt="24px"
            mb="20px"
            width="100%"
            p="16px"
            border="1px solid"
            borderColor="brand.gray600"
            borderRadius="4px"
          >
            {risk.infos.map((i, k) => (
              <Box as="p" mb="8px" key={k}>
                {i}
              </Box>
            ))}
          </Box>
        </Flex>
      );
    }
  }
  return (
    <Flex alignItems="flex-start" marginTop="1rem" width="100%" direction="column">
      <Heading color="brand.tertiary" mb="24px" size="md" as="h3">
        Demais Recomendações
      </Heading>
      <Grid templateColumns="repeat(4, 1fr)">
        <GridItem>
          <Label>Objeto do Contrato:</Label>
          <p>{ctx.data.areaResponsable}</p>
          <Box mt="1rem" />
          <Label>Data de Assinatura:</Label>
          <p>{ctx.data.receiveDate || 'Contrato não assinado'}</p>
        </GridItem>
        <GridItem>
          <Label>Contratante:</Label>
          <p>{ctx.data.companyName}</p>
          <Box mt="1rem" />
          <Label>Data de Expiração:</Label>
          <p>{ctx.data.reviewDate || 'Prazo Indeterminado'}</p>
        </GridItem>
        <GridItem colSpan={2}>
          <Label>Contratado:</Label>
          <p>{ctx.data.contact}</p>
          <Box mt="1rem" />
          <Label>Lista de Dados Pessoais Envolvidos na Contratação:</Label>
          <p>{hide ? pieceText + (fullText.length > 195 ? '...' : '') : fullText}</p>
          {fullText.length > 195 && (
            <Sbutton onClick={() => toggleHide()}>{hide ? 'Ver Todos' : 'Esconder'}</Sbutton>
          )}
        </GridItem>
      </Grid>
      <Box my="24px" width="100%" height="1px" background={colors.brand[600]} />
      <Heading color="brand.tertiary" mb="24px" size="md" as="h3">
        Maturidade do terceiro
      </Heading>
      <Flex direction="row">
        <PontGraph pos={ctx.results?.maturityScore.result || 0} start="right">
          <img alt="Velocimetro" src={velImg} />
          <img className="pointer" alt="Ponteiro" src={pontImg} />
          <span className="result">
            <Box>
              <Heading as="h4" size="sm">
                {maturity.text}
              </Heading>
              <Heading as="h4" size="lg" color={colors.brand[maturity.color]}>
                {parseFloat(ctx.results?.maturityScore.result || '0').toFixed(2)}%
              </Heading>
            </Box>
          </span>
        </PontGraph>
      </Flex>
      <Heading color="brand.tertiary" mt="64px" as="h4" fontSize="18px" mb="16px">
        Controlador/Operador:
      </Heading>
      <Flex gridGap="60px" mb="16px" alignItems="flex-start" direction="row">
        <Box>
          <Label>Posição Exercida pela {ctx.client?.name}:</Label>
          <p>{ctx.results?.companyPositionResult}</p>
        </Box>
        <Box>
          <Label>Posição Exercida pela outra parte:</Label>
          <p>{ctx.results?.thirdPartyPositionResult}</p>
        </Box>
      </Flex>
      <Box
        mb="32px"
        width="100%"
        p="16px"
        border="1px solid"
        borderColor="brand.gray600"
        borderRadius="4px"
      >
        {scenery.map((i, k) => (
          <Box as="p" mb="8px" key={k}>
            {i.replaceAll('{{client}}', ctx.client?.name || '')}
          </Box>
        ))}

        <Box as="p">
          Pelo trabalho de mapeamento realizado até o momento (mês/ano) na {ctx.client?.name}, não
          identificaram-se situações em que esse cenário se aplicaria. Contudo, na eventualidade de
          surgirem, seguem nossas percepções abaixo:Como operador, a {ctx.client?.name} goza de
          maior conforto em relação ao grau de responsabilidade assumido.Nesse contexto, entendemos
          que a {ctx.client?.name} deve negociar contratos a fim de repelir o aumento de
          responsabilidade natural do operador (atenção para questões que podem ser atribuídas à{' '}
          {ctx.client?.name}, como: coleta de consentimento, atendimento aos direitos dos titulares,
          realização de relatório de impacto e comunicação em caso de incidentes).{' '}
          <b>
            Seja qual for o resultado, é recomendável: Avaliar a criticidade do contrato (Teste de
            Risco do Contrato)Validar este documento e suas conclusões com o Encarregado pelo
            Tratamento de Dados Pessoais (DPO) da {ctx.client?.name}
          </b>
        </Box>
      </Box>
      <Heading color="brand.tertiary" as="h4" fontSize="18px" mb="24px">
        Risco do Contrato:
      </Heading>
      <Flex direction="row">
        <PontGraph pos={ctx.results?.contractRiskResult.result || 0} start="left">
          <img alt="Velocimetro" src={velImg} />
          <img className="pointer" alt="Ponteiro" src={pontImg} />
          <span className="result">
            <Box>
              <Heading as="h4" size="sm">
                {risk.text}
              </Heading>
              <Heading as="h4" size="lg" color={colors.brand[risk.color]}>
                {parseFloat(ctx.results?.contractRiskResult.result || '0').toFixed(2)}%
              </Heading>
            </Box>
          </span>
        </PontGraph>
      </Flex>
      {+parseFloat(ctx.results?.contractRiskResult.result || '0').toFixed(2) >= 50 && (
        <WarningMessage
          mt="31"
          text={'Considerar realização de Relatório de Impacto de Proteção de Dados Pessoais'}
        />
      )}
      <Box
        mt="24px"
        mb="20px"
        width="100%"
        p="16px"
        border="1px solid"
        borderColor="brand.gray600"
        borderRadius="4px"
      >
        {risk.infos.map((i, k) => (
          <Box as="p" mb="8px" key={k}>
            {i}
          </Box>
        ))}
      </Box>
    </Flex>
  );
};

export const AnalysisReport = memo(Item);
// export const AnalysisReport = memo(Item);
